import { apiClient, setAccessToken } from './client';

/**
 * Auth API surface that the AuthContext + auth pages call.
 *
 * The access token returned by login / refresh / accept-invite is
 * passed to setAccessToken() so the axios interceptor attaches it to
 * subsequent requests. Refresh tokens are managed by the server as
 * HttpOnly cookies; JS never touches them.
 */

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  /**
   * True only on the user's first-ever interactive login (LOGIN-LANDING).
   * The login flow routes a first-timer to /profile for onboarding and
   * everyone else to their intended destination (default /dashboard).
   * Absent/false on refresh and older backends.
   */
  first_login?: boolean;
}

export async function register(body: {
  email: string;
  password: string;
  full_name: string;
  /** LEGAL-TERMS: the current versions ticked (empty before any exist). */
  legal_document_ids?: string[];
}): Promise<void> {
  await apiClient.post('/api/v1/auth/register', body);
}

export async function verifyEmail(token: string): Promise<void> {
  await apiClient.post('/api/v1/auth/verify-email', { token });
}

/**
 * `/login` now returns either a TokenResponse (no TOTP) or a TOTP
 * challenge the caller must redeem at `/login/totp` with a 6-digit
 * code. Callers branch on the `requires_totp` discriminator: when
 * true, the response is a `TOTPLoginChallenge` and no access token
 * is set yet; the caller flips into the TOTP-input step.
 */
export interface TOTPLoginChallenge {
  requires_totp: true;
  challenge_token: string;
  expires_in: number;
}

export type LoginResponse = TokenResponse | TOTPLoginChallenge;

export function isTotpChallenge(
  r: LoginResponse,
): r is TOTPLoginChallenge {
  return (r as TOTPLoginChallenge).requires_totp === true;
}

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>(
    '/api/v1/auth/login',
    { email, password },
  );
  // Only prime the access token when this branch is a TokenResponse —
  // a challenge has no access_token to set.
  if (!isTotpChallenge(data)) {
    setAccessToken(data.access_token);
  }
  return data;
}

/**
 * Second step of the two-step login. Pass either the 6-digit live
 * code or one of the enrollment-time backup codes (single use).
 */
export async function loginTotp(
  challengeToken: string,
  opts: { code?: string; backupCode?: string },
): Promise<TokenResponse> {
  const { data } = await apiClient.post<TokenResponse>(
    '/api/v1/auth/login/totp',
    {
      challenge_token: challengeToken,
      code: opts.code,
      backup_code: opts.backupCode,
    },
  );
  setAccessToken(data.access_token);
  return data;
}

export async function refresh(): Promise<TokenResponse | null> {
  try {
    const { data } = await apiClient.post<TokenResponse>('/api/v1/auth/refresh');
    setAccessToken(data.access_token);
    return data;
  } catch {
    // Any failure here means the user has to log in again — clear
    // the in-memory token so the next protected call doesn't try
    // a stale one.
    setAccessToken(null);
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/api/v1/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

export async function resendVerification(
  email: string,
  password: string,
): Promise<void> {
  await apiClient.post('/api/v1/auth/resend-verification', { email, password });
}

/**
 * Kick off a password reset for `email`. The server always returns
 * 200 regardless of whether the address is registered — we don't
 * want to be an account-existence oracle — so this resolves either
 * way. The portal shows the same "if there's an account, an email is
 * on its way" copy in both branches.
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post('/api/v1/auth/password/forgot', { email });
}

/**
 * Exchange a reset token + new password. On success the server has
 * already issued a fresh refresh + access pair (sets the refresh as
 * an HttpOnly cookie); we prime setAccessToken so the caller can
 * redirect to a protected route without an extra refresh round-trip.
 */
export async function resetPassword(
  token: string,
  password: string,
): Promise<TokenResponse> {
  const { data } = await apiClient.post<TokenResponse>(
    '/api/v1/auth/password/reset',
    { token, password },
  );
  setAccessToken(data.access_token);
  return data;
}

/**
 * Change the signed-in user's password. Requires the current password
 * (re-verified server-side). 204 on success; 400 if the current
 * password is wrong; 422 if the new one fails the complexity policy.
 * The current session stays valid — no token rotation.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post('/api/v1/auth/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}
